import { useState, useMemo } from 'react';
import {
  Button,
  Badge,
  Checkbox,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@aaas/shared';
import { Users, X, Check } from 'lucide-react';

interface AccessMatrixUser {
  id: number;
  display_name: string | null;
  email: string;
  avatar_url: string | null;
}

interface UserSelectorProps {
  users: AccessMatrixUser[];
  selectedUserIds: number[];
  onSelectionChange: (userIds: number[]) => void;
}

export function UserSelector({ users, selectedUserIds, onSelectionChange }: UserSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter users based on search and limit to 10
  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users.slice(0, 10);
    const query = searchQuery.toLowerCase();
    return users
      .filter(
        (user) =>
          user.display_name?.toLowerCase().includes(query) ||
          user.email.toLowerCase().includes(query)
      )
      .slice(0, 10);
  }, [users, searchQuery]);

  const toggleUser = (userId: number) => {
    if (selectedUserIds.includes(userId)) {
      onSelectionChange(selectedUserIds.filter((id) => id !== userId));
    } else {
      onSelectionChange([...selectedUserIds, userId]);
    }
  };

  const selectedUsers = users.filter((u) => selectedUserIds.includes(u.id));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start min-h-10 h-auto py-2">
              {selectedUserIds.length === 0 ? (
                <>
                  <Users className="mr-2 h-4 w-4 flex-shrink-0" />
                  Select users to display...
                </>
              ) : (
                <div className="flex items-center gap-2 flex-wrap w-full">
                  <Users className="h-4 w-4 flex-shrink-0" />
                  {selectedUsers.map((user) => (
                    <Badge key={user.id} variant="secondary" className="gap-1 pr-1">
                      {user.avatar_url && (
                        <img
                          src={user.avatar_url}
                          alt={user.display_name || user.email}
                          className="w-4 h-4 rounded-full"
                        />
                      )}
                      <span>{user.email}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-4 w-4 p-0 hover:bg-transparent"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleUser(user.id);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="p-0"
            align="start"
            style={{ width: 'var(--radix-popover-trigger-width)' }}
          >
            <Command>
              <CommandInput
                placeholder="Search users by name or email..."
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
              <CommandList>
                <CommandEmpty>No users found.</CommandEmpty>
                <CommandGroup
                  heading={`Slack Users${filteredUsers.length === 10 ? ' (showing first 10)' : ''}`}
                >
                  {filteredUsers.map((user) => {
                    const isSelected = selectedUserIds.includes(user.id);
                    return (
                      <CommandItem key={user.id} onSelect={() => toggleUser(user.id)}>
                        <Checkbox
                          checked={isSelected}
                          className="mr-2"
                          onCheckedChange={() => toggleUser(user.id)}
                        />
                        <div className="flex items-center gap-2 flex-1">
                          {user.avatar_url && (
                            <img
                              src={user.avatar_url}
                              alt={user.display_name || user.email}
                              className="w-6 h-6 rounded-full"
                            />
                          )}
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {user.display_name || user.email.split('@')[0]}
                            </span>
                            <span className="text-xs text-muted-foreground">{user.email}</span>
                          </div>
                        </div>
                        {isSelected && <Check className="ml-auto h-4 w-4" />}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {selectedUserIds.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => onSelectionChange([])}>
            Clear All
          </Button>
        )}
      </div>
    </div>
  );
}
